import React from "react";

/* Tree rows for DataTable (`getSubRows`). Split from DataTable.tsx so the table
   file carries only the wiring: this owns the expansion state (uncontrolled or
   the controlled `expandedIds` pair), the flattening of the tree into the
   visible rows in sibling-sorted order, the APG treegrid row attributes and
   the first-cell ArrowRight / ArrowLeft rules. */

/** One visible row of the tree, in render order. */
export interface DataTableTreeNode<Row> {
  readonly row: Row;
  readonly id: string;
  /** 1-based depth: a top-level row is level 1. */
  readonly level: number;
  /** How many siblings share this row's parent, itself included. */
  readonly setSize: number;
  /** 1-based position among those siblings, after sorting. */
  readonly posInSet: number;
  /** The parent's index in the visible rows; null at the top level. */
  readonly parentIndex: number | null;
  readonly hasChildren: boolean;
  readonly expanded: boolean;
}

export interface UseDataTableTreeOptions<Row> {
  data: ReadonlyArray<Row>;
  getRowId: (row: Row) => string;
  /** Turns tree mode on. Undefined leaves the table flat. */
  getSubRows: ((row: Row) => ReadonlyArray<Row> | undefined) | undefined;
  /** Orders one set of siblings (the table's active sort). */
  sortRows: (rows: ReadonlyArray<Row>) => ReadonlyArray<Row>;
  defaultExpanded: boolean | ReadonlyArray<string> | undefined;
  expandedIds: ReadonlyArray<string> | undefined;
  onExpandedChange: ((ids: string[]) => void) | undefined;
  /** Props tree mode ignores, each warned about once while set. */
  ignored: Readonly<Record<string, boolean>>;
}

export interface DataTableTree<Row> {
  /** The visible rows, or null when tree mode is off. */
  readonly nodes: ReadonlyArray<DataTableTreeNode<Row>> | null;
  readonly toggle: (id: string) => void;
  /**
   * The first-cell keys (APG treegrid) for the visible row at `index`: null
   * when the key is not a tree key there, -1 when it expanded or collapsed the
   * row in place, or the index of the parent row to move focus to.
   */
  readonly onFirstCellKey: (key: string, index: number) => number | null;
}

/* "only": exactly these ids are open. "except": every parent is open except
   these, which is how `defaultExpanded={true}` keeps rows that arrive after
   the first render (a load finishing) open too. */
interface Expansion {
  readonly mode: "only" | "except";
  readonly ids: ReadonlySet<string>;
}

const warned = new Set<string>();

const isOpen = (e: Expansion, id: string) =>
  e.mode === "only" ? e.ids.has(id) : !e.ids.has(id);

/** The APG treegrid attributes of a row; parents carry aria-expanded. */
export function treeRowProps<Row>(node: DataTableTreeNode<Row>) {
  return {
    "aria-level": node.level,
    "aria-setsize": node.setSize,
    "aria-posinset": node.posInSet,
    "aria-expanded": node.hasChildren ? node.expanded : undefined,
  };
}

export function useDataTableTree<Row>({
  data,
  getRowId,
  getSubRows,
  sortRows,
  defaultExpanded = false,
  expandedIds,
  onExpandedChange,
  ignored,
}: UseDataTableTreeOptions<Row>): DataTableTree<Row> {
  const enabled = getSubRows !== undefined;
  const [internal, setInternal] = React.useState<Expansion>(() =>
    defaultExpanded === true
      ? { mode: "except", ids: new Set() }
      : {
          mode: "only",
          ids: new Set(defaultExpanded === false ? [] : defaultExpanded),
        },
  );
  const expansion = React.useMemo<Expansion>(
    () =>
      expandedIds !== undefined
        ? { mode: "only", ids: new Set(expandedIds) }
        : internal,
    [expandedIds, internal],
  );

  const nodes = React.useMemo(() => {
    if (!getSubRows) return null;
    const out: DataTableTreeNode<Row>[] = [];
    const walk = (
      rows: ReadonlyArray<Row>,
      level: number,
      parentIndex: number | null,
    ) => {
      const siblings = sortRows(rows);
      siblings.forEach((row, i) => {
        const id = getRowId(row);
        const children = getSubRows(row) ?? [];
        const hasChildren = children.length > 0;
        const expanded = hasChildren && isOpen(expansion, id);
        const index = out.length;
        out.push({
          row,
          id,
          level,
          setSize: siblings.length,
          posInSet: i + 1,
          parentIndex,
          hasChildren,
          expanded,
        });
        if (expanded) walk(children, level + 1, index);
      });
    };
    walk(data, 1, null);
    return out;
  }, [data, getRowId, getSubRows, sortRows, expansion]);

  /* Every parent id in the whole tree, collapsed branches included: what
     `onExpandedChange` reports while `defaultExpanded={true}` holds sway. */
  const parentIds = () => {
    const ids: string[] = [];
    const walk = (rows: ReadonlyArray<Row>) =>
      rows.forEach((row) => {
        const children = getSubRows?.(row) ?? [];
        if (children.length === 0) return;
        ids.push(getRowId(row));
        walk(children);
      });
    walk(data);
    return ids;
  };

  const setExpanded = (id: string, open: boolean) => {
    if (isOpen(expansion, id) === open) return;
    // "except" lists the closed rows, "only" the open ones.
    const listed = (expansion.mode === "only") === open;
    const ids = new Set(expansion.ids);
    if (listed) ids.add(id);
    else ids.delete(id);
    const next: Expansion = { mode: expansion.mode, ids };
    if (expandedIds === undefined) setInternal(next);
    onExpandedChange?.(
      next.mode === "only"
        ? [...ids]
        : parentIds().filter((parent) => !ids.has(parent)),
    );
  };

  const toggle = (id: string) => setExpanded(id, !isOpen(expansion, id));

  const onFirstCellKey = (key: string, index: number): number | null => {
    const node = nodes?.[index];
    if (!node) return null;
    if (key === "ArrowRight" && node.hasChildren && !node.expanded) {
      setExpanded(node.id, true);
      return -1;
    }
    if (key !== "ArrowLeft") return null;
    if (node.expanded) {
      setExpanded(node.id, false);
      return -1;
    }
    return node.parentIndex;
  };

  const ignoredKeys = Object.keys(ignored)
    .filter((prop) => ignored[prop])
    .join(",");
  React.useEffect(() => {
    if (!enabled || ignoredKeys === "") return;
    for (const prop of ignoredKeys.split(",")) {
      if (warned.has(prop)) continue;
      warned.add(prop);
      console.warn(
        `[DataTable] \`${prop}\` is ignored in tree mode (\`getSubRows\`): ` +
          "a tree table renders every visible row in one treegrid.",
      );
    }
  }, [enabled, ignoredKeys]);

  return { nodes, toggle, onFirstCellKey };
}
